import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const DL_STATUS = {
  expire: { label: 'Expiré', cls: 'bg-red-600 text-white' },
  urgent: { label: 'Urgent', cls: 'bg-orange-500 text-white' },
  a_prevoir: { label: 'À prévoir', cls: 'bg-yellow-100 text-yellow-800' },
  a_jour: { label: 'À jour', cls: 'bg-green-100 text-green-700' },
};

const SECTIONS = {
  insurance: { label: 'Assurance', fields: [['start_date','Début','date'],['end_date','Fin','date'],['company','Compagnie','text'],['contract_number','N° contrat','text'],['comment','Commentaire','text']] },
  technical_control: { label: 'Contrôle technique', fields: [['last_date','Dernier contrôle','date'],['valid_from','Début validité','date'],['valid_until','Fin validité','date'],['result','Résultat','text'],['counter_visit_required','Contre-visite requise (true/false)','text'],['counter_visit_deadline','Date limite contre-visite','date']] },
  antipollution: { label: 'Antipollution', fields: [['last_date','Dernier contrôle','date'],['valid_until','Fin validité','date'],['result','Résultat','text']] },
  registration: { label: 'Carte grise / plaque provisoire', fields: [['start_date','Début','date'],['end_date','Fin','date'],['plate','Plaque concernée','text'],['plate_change_planned_date','Changement plaque prévu','date'],['plate_change_reason','Motif','text'],['new_plate','Nouvelle plaque','text']] },
  revision: { label: 'Révision', fields: [['last_date','Dernière révision','date'],['last_km','Km dernière révision','number'],['next_date','Prochaine (date) *FM','date'],['next_km','Prochaine (km) *FM','number'],['garage','Garage','text'],['comment','Commentaire','text']] },
  oil_change: { label: 'Vidange', fields: [['last_date','Dernière vidange','date'],['last_km','Km dernière vidange','number'],['next_date','Prochaine (date) *FM','date'],['next_km','Prochaine (km) *FM','number'],['comment','Commentaire','text']] },
};

const DocsDialog = ({ vehicle, open, onClose, onSaved }) => {
  const [section, setSection] = useState('insurance');
  const [data, setData] = useState({});
  useEffect(() => { if (vehicle) setData(vehicle[section] || {}); }, [vehicle, section]);
  if (!open) return null;
  const save = async () => {
    try {
      const res = await axios.put(`${API}/fleet/vehicles/${vehicle.vehicle_id}/documents`, { section, data }, { withCredentials: true });
      alert(res.data.message + ` (${res.data.alerts_closed} alerte(s) clôturée(s))`);
      onSaved?.();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="docs-dialog">
        <DialogHeader><DialogTitle>Documents & entretiens — {vehicle?.plate_number}</DialogTitle></DialogHeader>
        <select className="w-full px-3 py-2 border rounded-md bg-white text-sm" value={section} onChange={(e) => setSection(e.target.value)} data-testid="docs-section-select">
          {Object.entries(SECTIONS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
        </select>
        <div className="space-y-2">
          {SECTIONS[section].fields.map(([key, label, type]) => (
            <div key={key}>
              <Label className="text-xs">{label}</Label>
              <Input type={type === 'number' ? 'number' : type} value={data[key] ?? ''}
                onChange={(e) => setData({ ...data, [key]: type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : (key === 'counter_visit_required' ? e.target.value === 'true' : e.target.value) })}
                data-testid={`doc-field-${key}`} />
            </div>
          ))}
        </div>
        <Button className="w-full" onClick={save} data-testid="docs-save-btn">Enregistrer</Button>
      </DialogContent>
    </Dialog>
  );
};

const DeadlinesTab = ({ vehicles, sites, onRefreshParc }) => {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ site: 'all', type: 'all', status: 'all', search: '' });
  const [docsVehicle, setDocsVehicle] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/fleet/deadlines`, { withCredentials: true });
      setRows(res.data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const types = [...new Set(rows.map(r => r.type))];
  const filtered = rows.filter(r =>
    (filters.site === 'all' || r.site_id === filters.site) &&
    (filters.type === 'all' || r.type === filters.type) &&
    (filters.status === 'all' ||
      (filters.status === 'j30' ? (r.days_left !== null && r.days_left >= 0 && r.days_left <= 30)
        : filters.status === 'j7' ? (r.days_left !== null && r.days_left >= 0 && r.days_left <= 7)
          : r.status === filters.status)) &&
    (!filters.search || (r.plate_number || '').toLowerCase().includes(filters.search.toLowerCase()))
  );

  return (
    <div className="space-y-4" data-testid="deadlines-tab">
      <div className="flex flex-wrap gap-2 items-center">
        <Input placeholder="Plaque..." className="w-40" value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} data-testid="dl-search" />
        <select className="px-2 py-2 border rounded-md bg-white text-sm" value={filters.site} onChange={(e) => setFilters({ ...filters, site: e.target.value })}>
          <option value="all">Tous sites</option>{sites.map(s => <option key={s.site_id} value={s.site_id}>{s.name}</option>)}
        </select>
        <select className="px-2 py-2 border rounded-md bg-white text-sm" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} data-testid="dl-type-filter">
          <option value="all">Tous types</option>{types.map(t => <option key={t} value={t}>{rows.find(r => r.type === t)?.label}</option>)}
        </select>
        <select className="px-2 py-2 border rounded-md bg-white text-sm" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="dl-status-filter">
          <option value="all">Tous statuts</option>
          <option value="j30">Moins de 30 j</option>
          <option value="j7">Moins de 7 j</option>
          <option value="expire">Expiré</option>
          <option value="urgent">Urgent</option>
          <option value="a_prevoir">À prévoir</option>
          <option value="a_jour">À jour</option>
        </select>
        <span className="text-sm text-gray-500 ml-auto">{filtered.length} échéance(s)</span>
      </div>
      <div className="space-y-2">
        {filtered.map((r, i) => (
          <Card key={i} data-testid={`dl-row-${r.vehicle_id}-${r.type}`}>
            <CardContent className="p-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-mono font-bold">{r.plate_number} <span className="font-sans font-normal text-sm text-gray-600">— {r.label}</span></p>
                <p className="text-xs text-gray-500">
                  {r.last_date && `Dernière: ${r.last_date} · `}
                  {r.due_date && `Échéance: ${r.due_date}`}
                  {r.due_km && ` · Limite: ${r.due_km} km`}
                  {r.days_left !== null && ` · ${r.days_left >= 0 ? r.days_left + ' j restants' : 'dépassé de ' + Math.abs(r.days_left) + ' j'}`}
                  {r.km_left !== null && ` · ${r.km_left} km restants`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded font-bold ${DL_STATUS[r.status]?.cls}`}>{DL_STATUS[r.status]?.label}</span>
                <Button size="sm" variant="outline" onClick={() => setDocsVehicle(vehicles.find(v => v.vehicle_id === r.vehicle_id))} data-testid={`dl-edit-${r.vehicle_id}-${r.type}`}>Mettre à jour</Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && <p className="text-sm text-gray-400">Aucune échéance renseignée avec ces filtres</p>}
      </div>
      <DocsDialog vehicle={docsVehicle} open={!!docsVehicle} onClose={() => setDocsVehicle(null)}
        onSaved={() => { setDocsVehicle(null); load(); onRefreshParc?.(); }} />
    </div>
  );
};

export default DeadlinesTab;
